import { useState } from 'react';
import { Filter, X } from 'lucide-react';
import { clsx } from 'clsx';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator
} from '@/components/ui/DropdownMenu';
import { abschlussLabelBeide, bereitLabelBeide } from '@/lib/zustandLabel';
import type { DashboardSearch } from '@/types/dashboard';
import type {
  ErfasserOption,
  VerlagOption,
  VersandartOption,
  ZustandOption
} from '@/types/dashboard';
import { FOKUS_RING } from '@/lib/fokus';

type Props = {
  /** data-tour-Name fuer die Einfuehrungstour. */
  anker?: string;
  search: DashboardSearch;
  onChange: (patch: Partial<DashboardSearch>) => void;
  zustaende: ZustandOption[];
  erfasser: ErfasserOption[];
  verlage: VerlagOption[];
  versandarten: VersandartOption[];
};

function csvToggle(csv: string | undefined, value: string): string {
  const arr = csv && csv !== '' ? csv.split(',') : [];
  const next = arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
  return next.join(',');
}

function csvIncludes(csv: string | undefined, value: string): boolean {
  if (!csv) return false;
  return csv.split(',').includes(value);
}

export function DashboardFilterBar({
  anker,
  search,
  onChange,
  zustaende,
  erfasser,
  verlage,
  versandarten
}: Props) {
  const [verlagSearch, setVerlagSearch] = useState('');
  const verlageGefiltert = verlage.filter((v) =>
    v.label.toLowerCase().includes(verlagSearch.toLowerCase())
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-tour={anker}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-md border border-line bg-cream px-3 py-2 text-sm text-ink-soft hover:border-akzent hover:text-akzent"
        >
          <Filter className="h-4 w-4" />
          Filter
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="max-h-[70vh] w-[20rem] overflow-y-auto">
        <DropdownMenuLabel>Schnellfilter</DropdownMenuLabel>
        <ToggleRow
          label="Nur meine"
          active={!!search.mein}
          onToggle={() => onChange({ mein: !search.mein })}
        />
        <ToggleRow
          label="Nur überfällig"
          active={!!search.nur_ueberfaellig}
          onToggle={() => onChange({ nur_ueberfaellig: !search.nur_ueberfaellig })}
        />
        <ToggleRow
          label="Beschaffung offen"
          active={!!search.beschaffung_offen}
          onToggle={() => onChange({ beschaffung_offen: !search.beschaffung_offen })}
        />
        <ToggleRow
          label="Nachlieferung offen"
          active={!!search.nachlieferung}
          onToggle={() => onChange({ nachlieferung: !search.nachlieferung })}
        />
        {/* HIER STAND „Archivierte einbeziehen" (A10.4)
            Der Schalter mischte Erledigtes unter die offenen Vorgaenge und
            zeigte dabei nur 90 Tage zurueck — fuer eine Suche zu wenig, fuer
            das Arbeitsbrett zu viel. Beides hat jetzt seinen eigenen Ort:
            das Archiv als eigene Ansicht (ohne Frist), und die Suche
            liefert Archiv-Treffer ohnehin immer mit, als abgesetzte
            Gruppe. Ein Schalter, den man erst finden muss, um etwas zu
            sehen, das es gibt, war der Umweg. */}

        <DropdownMenuSeparator />
        <DropdownMenuLabel>Zustand</DropdownMenuLabel>
        {/* A12.8: „Bereit" und „Abgeschlossen" heissen im Filter beides
            zugleich. Der Filter spricht nicht ueber eine Bestellung,
            sondern ueber alle — und darunter sind Abholungen wie
            Versandvorgaenge. Die Zahl bleibt dieselbe, gefiltert wird
            weiter nach dem einen Zustandscode. */}
        {zustaende.map((z) => (
          <ToggleRow
            key={z.code}
            label={`${zustandFilterLabel(z.code, z.label)} · ${z.count}`}
            active={csvIncludes(search.zustaende, z.code)}
            onToggle={() => onChange({ zustaende: csvToggle(search.zustaende, z.code) })}
          />
        ))}

        <DropdownMenuSeparator />
        <DropdownMenuLabel>Mitarbeiter</DropdownMenuLabel>
        {erfasser.map((e) => (
          <ToggleRow
            key={e.username}
            label={`${e.name} · ${e.count}`}
            active={csvIncludes(search.erfasser, e.username)}
            onToggle={() => onChange({ erfasser: csvToggle(search.erfasser, e.username) })}
          />
        ))}

        <DropdownMenuSeparator />
        <DropdownMenuLabel>Versandart</DropdownMenuLabel>
        {versandarten.map((va) => (
          <ToggleRow
            key={va.id}
            label={`${va.label} · ${va.count}`}
            active={search.versandart_id === va.id}
            onToggle={() =>
              onChange({ versandart_id: search.versandart_id === va.id ? undefined : va.id })
            }
          />
        ))}

        <DropdownMenuSeparator />
        <DropdownMenuLabel>Verlag</DropdownMenuLabel>
        <div className="px-3 py-1">
          <input
            type="search"
            value={verlagSearch}
            onChange={(e) => setVerlagSearch(e.target.value)}
            placeholder="Verlag filtern …"
            className={`min-h-[44px] w-full rounded-md border border-line bg-cream px-2 py-1 text-xs ${FOKUS_RING}`}
          />
        </div>
        {verlageGefiltert.slice(0, 25).map((v) => (
          <ToggleRow
            key={v.id}
            label={`${v.label} · ${v.count}`}
            active={search.verlag_id === v.id}
            onToggle={() =>
              onChange({ verlag_id: search.verlag_id === v.id ? undefined : v.id })
            }
          />
        ))}

        <DropdownMenuSeparator />
        <DropdownMenuLabel>Erfassungs-Alter</DropdownMenuLabel>
        <div className="grid grid-cols-2 gap-2 px-3 pb-2">
          <NumberInput
            label="älter als (Tage)"
            value={search.alter_min}
            onChange={(v) => onChange({ alter_min: v })}
          />
          <NumberInput
            label="jünger als (Tage)"
            value={search.alter_max}
            onChange={(v) => onChange({ alter_max: v })}
          />
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ToggleRow({
  label,
  active,
  onToggle
}: {
  label: string;
  active: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={clsx(
        'flex min-h-[44px] w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-cream-dark',
        active && 'bg-akzent-sanft text-akzent-stark'
      )}
    >
      <span
        aria-hidden
        className={clsx(
          'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border',
          active ? 'border-akzent' : 'border-line'
        )}
      >
        {active && <span className="h-2 w-2 rounded-sm bg-akzent" />}
      </span>
      <span className="truncate">{label}</span>
    </button>
  );
}

function NumberInput({
  label,
  value,
  onChange
}: {
  label: string;
  value: number | undefined;
  onChange: (v: number | undefined) => void;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs text-ink-soft">
      <span>{label}</span>
      <input
        type="number"
        // Tage sind ganze Zahlen — Zifferntastatur ohne Komma.
        inputMode="numeric"
        value={value ?? ''}
        min={0}
        onChange={(e) => {
          const v = e.target.value === '' ? undefined : Math.max(0, Number(e.target.value));
          onChange(Number.isFinite(v as number) ? (v as number) : undefined);
        }}
        className={`min-h-[44px] rounded-md border border-line bg-cream px-2 py-1 text-sm ${FOKUS_RING}`}
      />
    </label>
  );
}

export function ActiveFilterChips({
  search,
  onChange,
  zustaende,
  erfasser,
  verlage,
  versandarten
}: {
  search: DashboardSearch;
  onChange: (patch: Partial<DashboardSearch>) => void;
  zustaende: ZustandOption[];
  erfasser: ErfasserOption[];
  verlage: VerlagOption[];
  versandarten: VersandartOption[];
}) {
  const chips: { label: string; clear: () => void }[] = [];
  if (search.mein) chips.push({ label: 'Nur meine', clear: () => onChange({ mein: false }) });
  if (search.nur_ueberfaellig)
    chips.push({ label: 'Nur überfällig', clear: () => onChange({ nur_ueberfaellig: false }) });
  if (search.beschaffung_offen)
    chips.push({
      label: 'Beschaffung offen',
      clear: () => onChange({ beschaffung_offen: false })
    });
  if (search.nachlieferung)
    chips.push({ label: 'Nachlieferung offen', clear: () => onChange({ nachlieferung: false }) });
  if (search.zustaende && search.zustaende !== '') {
    const codes = search.zustaende.split(',');
    const labels = codes
      .map((c) => zustaende.find((z) => z.code === c)?.label ?? c)
      .join(', ');
    chips.push({ label: `Zustand: ${labels}`, clear: () => onChange({ zustaende: undefined }) });
  }
  if (search.erfasser && search.erfasser !== '') {
    const usrs = search.erfasser.split(',');
    const labels = usrs
      .map((u) => erfasser.find((e) => e.username === u)?.name ?? u)
      .join(', ');
    chips.push({
      label: `Erfasser: ${labels}`,
      clear: () => onChange({ erfasser: undefined })
    });
  }
  if (search.verlag_id !== undefined) {
    const v = verlage.find((x) => x.id === search.verlag_id);
    chips.push({
      label: `Verlag: ${v?.label ?? search.verlag_id}`,
      clear: () => onChange({ verlag_id: undefined })
    });
  }
  if (search.versandart_id !== undefined) {
    const va = versandarten.find((x) => x.id === search.versandart_id);
    chips.push({
      label: `Versand: ${va?.label ?? search.versandart_id}`,
      clear: () => onChange({ versandart_id: undefined })
    });
  }
  if (search.alter_min !== undefined) {
    chips.push({
      label: `älter ${search.alter_min} T.`,
      clear: () => onChange({ alter_min: undefined })
    });
  }
  if (search.alter_max !== undefined) {
    chips.push({
      label: `jünger ${search.alter_max} T.`,
      clear: () => onChange({ alter_max: undefined })
    });
  }

  if (chips.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 border-b border-line bg-cream-warm px-4 py-2">
      {chips.map((c, i) => (
        <button
          key={i}
          type="button"
          onClick={c.clear}
          className="inline-flex items-center gap-1.5 rounded-full border border-akzent-linie bg-akzent-sanft px-3 py-1 text-xs text-akzent-stark hover:bg-akzent-linie"
        >
          {c.label}
          <X className="h-3 w-3" />
        </button>
      ))}
    </div>
  );
}

/**
 * Wie der Zustand im Filter heisst (A12.8). Zwei Codes tragen dort
 * beide Lesarten, alle anderen ihren gepflegten Namen aus der Tabelle.
 */
function zustandFilterLabel(code: string, label: string): string {
  if (code === 'bereit') return bereitLabelBeide();
  if (code === 'abgeschlossen') return abschlussLabelBeide();
  return label;
}
